import { SearchOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, CircularProgress, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import ButtonIcon from 'components/ButtonIcon';
import LanguageContext from 'context/LanguageContext';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { getAllTransactionsCSV } from 'services/admin-organizations';

interface TransactionFilterProps {
  onFilterChange: (filters: {
    revisionId?: string;
    projectId?: string;
  }) => void;
  isLoading?: boolean;
}

const TransactionFilter: React.FC<TransactionFilterProps> = ({ onFilterChange, isLoading = false }) => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const [revisionId, setRevisionId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [searchType, setSearchType] = useState('projectId');

  // React Query mutation for CSV export
  const exportCSVMutation = useMutation(async () => {
    return await getAllTransactionsCSV({
      revisionId: revisionId.trim() || undefined,
      projectId: projectId.trim() || undefined
    });
  });

  const handleDownloadCSV = async () => {
    try {
      const blob = await exportCSVMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transactions.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Optionally show error
    }
  };

  const handleChangeSearchText = (e: any) => {
    const value = e.target.value;
    if (searchType === 'revisionId') {
      setRevisionId(value);
      onFilterChange({
        revisionId: value.trim() || undefined,
        projectId: projectId.trim() || undefined
      });
    } else if (searchType === 'projectId') {
      setProjectId(value);
      onFilterChange({
        revisionId: revisionId.trim() || undefined,
        projectId: value.trim() || undefined
      });
    }
  };

  const handleSearchTypeChange = (e: any) => {
    setSearchType(e.target.value);
    // Clear the other field when switching search type
    if (e.target.value === 'revisionId') {
      setProjectId('');
      onFilterChange({
        revisionId: revisionId.trim() || undefined,
        projectId: undefined
      });
    } else {
      setRevisionId('');
      onFilterChange({
        revisionId: undefined,
        projectId: projectId.trim() || undefined
      });
    }
  };

  const handleClear = () => {
    setRevisionId('');
    setProjectId('');
    onFilterChange({});
  };

  const getCurrentValue = () => {
    return searchType === 'revisionId' ? revisionId : projectId;
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: "144px",
        mb: "22px",
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box display={'flex'} alignItems={"center"} gap={"2rem"} flexWrap={"nowrap"} width={{ xs: "100%", md: "80%" }} >
          <Box>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '24px',
                lineHeight: '31px',
                color: '#2F3B49',
              }}
            >
              {t('ADMIN_TRANSACTION_TITLE')}
            </Typography>
          </Box>
          
          <Box width={"100%"}>
            <TextField
              autoComplete='off'
              id='standard-bare'
              size='small'
              fullWidth={true}
              variant='outlined'
              placeholder={t('ADMIN_STATS_SEARCH_PLACEHOLDER')}
              sx={{
                width: { md: "100%", lg: "590px" },
                '& .MuiInputBase-root': {
                  WebkitFontSmoothing: 'auto',
                  padding: "0px",
                },
                '& .MuiInputBase-inputAdornedStart': {
                  padding: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FormControl sx={{ mr: 1 }}>
                      <Select
                        value={searchType}
                        onChange={handleSearchTypeChange}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              '& .MuiMenuItem-root': {
                                fontSize: '16px',
                              },
                            },
                          },
                        }}
                        sx={{
                          height: '50px',
                          width: language === 'he' ? '109px' : '129px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '4px 0px 0px 4px',
                          borderRight: '1px solid #C4C4C4',
                          '& .MuiSelect-select': {
                            padding: '8px 10px 8px 8px',
                            fontSize: '16px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="revisionId">{t('ADMIN_TRANSACTION_REVISION_ID')}</MenuItem>
                        <MenuItem value="projectId">{t('ADMIN_TRANSACTION_PROJECT_ID')}</MenuItem>
                      </Select>
                    </FormControl>
                    {getCurrentValue() ? (
                      <IconButton onClick={handleClear}>
                        <ClearIcon />
                      </IconButton>
                    ) : (
                      <IconButton disabled>
                        <SearchOutlined />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
                endAdornment: isLoading ? (
                  <Box display="flex" mx={1}>
                    <CircularProgress size={24} />
                  </Box>
                ) : null,
                sx: { height: '50px' }
              }}
              value={getCurrentValue()}
              onChange={handleChangeSearchText}
            />
          </Box>
        </Box>

        <Box display={'flex'} alignItems={"center"} gap={2}>
          <ButtonIcon
            onClick={handleDownloadCSV}
            disabled={exportCSVMutation.isLoading}
          >
            {exportCSVMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FileDownloadOutlinedIcon fontSize='large' />
            )}
          </ButtonIcon>
        </Box>
      </Box>
    </Box>
  );
};

export default TransactionFilter; 